import { S_IFDIR, S_IFREG } from '@zenfs/core/emulation/constants.js';
import { resolve } from '@zenfs/core/path';
import { Errno, ErrnoError } from '@zenfs/core/error.js';
import { NoSyncFile, isWriteable } from '@zenfs/core/file.js';
import { FileSystem } from '@zenfs/core/filesystem.js';
import { Readonly, Sync } from '@zenfs/core/mixins/index.js';
import { Stats } from '@zenfs/core/stats.js';
import { PrimaryVolumeDescriptor, SupplementaryVolumeDescriptor } from './VolumeDescriptor.js';
import { PXEntry, TFEntry } from './entries.js';
/**
 * Mounts an ISO file as a read-only file system.
 *
 * Supports:
 * * Vanilla ISO9660 ISOs
 * * Microsoft Joliet and Rock Ridge extensions to the ISO9660 standard
 */
export class IsoFS extends Readonly(Sync(FileSystem)) {
    data;
    _pvd;
    _root;
    _name;
    /**
     * **Deprecated. Please use IsoFS.Create() method instead.**
     *
     * Constructs a read-only file system from the given ISO.
     * @param data The ISO file in a buffer.
     * @param name The name of the ISO (optional; used for debug messages / identification via getName()).
     */
    constructor({ data, name = '' }) {
        super();
        this._name = name;
        this.data = data;
        // Skip first 16 sectors.
        let vdTerminatorFound = false;
        let i = 16 * 2048;
        const candidateVDs = new Array();
        while (!vdTerminatorFound && i < data.length) {
            const slice = this.data.slice(i);
            switch (slice[0]) {
                case 1 /* VolumeDescriptorType.Primary */:
                    candidateVDs.push(new PrimaryVolumeDescriptor(slice));
                    break;
                case 2 /* VolumeDescriptorType.Supplementary */:
                    candidateVDs.push(new SupplementaryVolumeDescriptor(slice));
                    break;
                case 255 /* VolumeDescriptorType.SetTerminator */:
                    vdTerminatorFound = true;
                    break;
            }
            i += 2048;
        }
        if (!candidateVDs.length) {
            throw new ErrnoError(Errno.EIO, 'Unable to find a suitable volume descriptor.');
        }
        for (const v of candidateVDs) {
            // Take an SVD over a PVD.
            if (this._pvd?.type != 2 /* VolumeDescriptorType.Supplementary */) {
                this._pvd = v;
            }
        }
        if (!this._pvd) {
            throw new ErrnoError(Errno.EINVAL, 'No primary volume descriptor');
        }
        this._root = this._pvd.rootDirectoryEntry(this.data);
    }
    metadata() {
        return {
            ...super.metadata(),
            name: ['iso', this._name, this._pvd?.name, this._root && this._root.hasRockRidge && 'RockRidge'].filter(e => e).join(':'),
            readonly: true,
            totalSpace: this.data.byteLength,
        };
    }
    statSync(path) {
        const record = this._getDirectoryRecord(path);
        if (!record) {
            throw ErrnoError.With('ENOENT', path, 'stat');
        }
        return this._getStats(path, record);
    }
    openFileSync(path, flag) {
        if (isWriteable(flag)) {
            // Cannot write to RO file systems.
            throw new ErrnoError(Errno.EPERM, path);
        }
        const record = this._getDirectoryRecord(path);
        if (!record) {
            throw ErrnoError.With('ENOENT', path, 'openFile');
        }
        if (record.isSymlink(this.data)) {
            return this.openFileSync(resolve(path, record.getSymlinkPath(this.data)), flag);
        }
        if (record.isDirectory(this.data)) {
            throw ErrnoError.With('EISDIR', path, 'openFile');
        }
        const data = record.getFile(this.data);
        const stats = this._getStats(path, record);
        return new NoSyncFile(this, path, flag, stats, new Uint8Array(data));
    }
    readdirSync(path) {
        // Check if it exists.
        const record = this._getDirectoryRecord(path);
        if (!record) {
            throw ErrnoError.With('ENOENT', path, 'readdir');
        }
        if (record.isDirectory(this.data)) {
            return Array.from(record.getDirectory(this.data).keys());
        }
        throw ErrnoError.With('ENOTDIR', path, 'readdir');
    }
    _getDirectoryRecord(path) {
        // Special case.
        if (path === '/') {
            return this._root;
        }
        const parts = path.split('/').slice(1);
        let dir = this._root;
        for (const part of parts) {
            if (!dir.isDirectory(this.data)) {
                return;
            }
            dir = dir.getDirectory(this.data).get(part);
            if (!dir) {
                return;
            }
        }
        return dir;
    }
    _getStats(path, record) {
        if (record.isSymlink(this.data)) {
            const newP = resolve(path, record.getSymlinkPath(this.data));
            const dirRec = this._getDirectoryRecord(newP);
            if (!dirRec) {
                return;
            }
            return this._getStats(newP, dirRec);
        }
        let mode = 0o555;
        const time = record.recordingDate.getTime();
        let atimeMs = time, mtimeMs = time, ctimeMs = time;
        if (record.hasRockRidge) {
            const entries = record.getSUEntries(this.data);
            for (const entry of entries) {
                if (entry instanceof PXEntry) {
                    mode = Number(entry.mode);
                    continue;
                }
                if (!(entry instanceof TFEntry)) {
                    continue;
                }
                const flags = entry.flags;
                if (flags & 4 /* TFFlag.ACCESS */) {
                    atimeMs = entry.access.getTime();
                }
                if (flags & 2 /* TFFlag.MODIFY */) {
                    mtimeMs = entry.modify.getTime();
                }
                if (flags & 1 /* TFFlag.CREATION */) {
                    ctimeMs = entry.creation.getTime();
                }
            }
        }
        // Mask out writeable flags. This is a RO file system.
        mode &= 0o555;
        return new Stats({
            mode: mode | (record.isDirectory(this.data) ? S_IFDIR : S_IFREG),
            size: record.dataLength,
            atimeMs,
            mtimeMs,
            ctimeMs,
        });
    }
}
const _Iso = {
    name: 'Iso',
    isAvailable() {
        return true;
    },
    options: {
        data: {
            type: 'object',
            required: true,
            validator(arg) {
                if (!(arg instanceof Uint8Array)) {
                    throw new TypeError('data is not an Uint8Array');
                }
            },
        },
    },
    create(options) {
        return new IsoFS(options);
    },
};
export const Iso = _Iso;
//# sourceMappingURL=fs.js.map